import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { RiAlertLine, RiHomeLine, RiRefreshLine } from 'react-icons/ri';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { resolveLandingRoute } from '../config/navigation';
import { buildMenuAccessChecker } from '../utils/permissions';

interface ErrorPageProps {
  statusCode?: number;
  title?: string;
  message?: string;
}

interface LocationState {
  statusCode?: number;
  title?: string;
  message?: string;
}

const ErrorPage: React.FC<ErrorPageProps> = (props) => {
  const location = useLocation();
  const state = location.state as LocationState;
  
  // Use state from navigation if available, otherwise use props or defaults
  const statusCode = state?.statusCode || props.statusCode || 500;
  const title = state?.title || props.title || 'เกิดข้อผิดพลาดในระบบ';
  const message = state?.message || props.message || 'ระบบกำลังประสบปัญหาขัดข้อง โปรดลองใหม่อีกครั้งหรือติดต่อผู้ดูแลระบบ';
  const navigate = useNavigate();

  // G-15: ปุ่มหน้าหลักชี้เมนูแรกที่ role นี้เข้าได้จริง (แบบเดียวกับ AccessDenied)
  // หน้านี้อาจ render จาก error boundary ตอน auth ยังไม่พร้อม — useAuth คืน safe defaults
  // (ไม่ throw) และเมื่อยังไม่ authenticated ให้คง /dashboard ตามเดิม
  const { isAuthenticated, permissions } = useAuth();
  const { isSuperAdmin, isSuperUser } = usePermission();
  const homeTarget = isAuthenticated
    ? resolveLandingRoute(buildMenuAccessChecker(permissions, isSuperAdmin || isSuperUser))
    : '/dashboard';

  const handleRefresh = () => {
    window.location.reload();
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-danger-600 via-danger-700 to-danger-800 flex flex-col justify-center items-center p-4">
      {/* Decorative Elements */}
      <div className="absolute inset-0 bg-grid-white/5" />
      <div className="absolute -top-24 -right-24 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-danger-400/20 rounded-full blur-3xl" />
      <div className="absolute top-1/3 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-2xl" />

      <div className="relative max-w-md w-full">
        {/* Error Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-32 h-32 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center border-4 border-white/20 shadow-2xl">
            <RiAlertLine className="text-white h-16 w-16" />
          </div>
        </div>

        {/* Error Content Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-white/20">
          <div className="p-8">
            <h1 className="text-5xl font-bold text-center text-danger-700 mb-3">{statusCode}</h1>
            <h2 className="text-2xl font-semibold text-center text-secondary-800 mb-4">{title}</h2>

            <p className="text-secondary-600 text-center mb-8 leading-relaxed">
              {message}
            </p>

            <div className="flex flex-col space-y-3">
              <Link
                to={homeTarget}
                className="group flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
              >
                <RiHomeLine className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform duration-300" />
                กลับไปยังหน้าหลัก
              </Link>

              <button
                onClick={handleRefresh}
                className="flex items-center justify-center px-6 py-3 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
              >
                <RiRefreshLine className="mr-2 h-5 w-5" />
                โหลดหน้าใหม่
              </button>

              <button
                onClick={() => window.history.back()}
                className="flex items-center justify-center px-6 py-3 border border-secondary-300 rounded-md shadow-sm text-sm font-medium text-secondary-700 bg-white hover:bg-secondary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all"
              >
                กลับไปยังหน้าก่อนหน้า
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 text-center text-sm text-white/90 backdrop-blur-sm bg-white/5 rounded-xl px-6 py-3 border border-white/10">
          หากปัญหายังคงอยู่ โปรดติดต่อทีมผู้ดูแลระบบ
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
